import type { Option } from '@/constants/institute';
import type { ParentAccountOutcome, StudentAccountOutcome } from './api/admissions.api';

/**
 * The fixed choices an admission form offers.
 *
 * They live with the feature rather than in `src/constants/institute.ts` because
 * nothing else uses them yet. Each list mirrors a Prisma enum of the same name;
 * when a second screen needs them they move up alongside the other shared
 * option lists.
 *
 * Nothing here is one institute's configuration — an institute's own routes, buses and
 * fee concessions are typed in, never baked into the build (CLAUDE.md §4).
 */

function options<T extends string>(entries: Record<T, string>): Option<T>[] {
  return (Object.entries(entries) as [T, string][]).map(([value, label]) => ({ value, label }));
}

// A person's gender and blood group are asked of staff as well as students,
// so they live with the shared option lists and are re-exported here.
export {
  BLOOD_GROUP_LABEL,
  BLOOD_GROUP_OPTIONS,
  GENDER_LABEL,
  GENDER_OPTIONS,
  type BloodGroup,
  type StudentGender,
} from '@/constants/institute';

/** The categories Indian institutes report against for RTE and scholarships. */
export const SOCIAL_CATEGORY_LABEL = {
  GENERAL: 'General',
  OBC: 'OBC',
  SC: 'SC',
  ST: 'ST',
  EWS: 'EWS',
} as const;
export type SocialCategory = keyof typeof SOCIAL_CATEGORY_LABEL;
export const SOCIAL_CATEGORY_OPTIONS = options(SOCIAL_CATEGORY_LABEL);

/**
 * Every admission type a stored record may hold. The quota types are no longer
 * offered, but records written before that still carry them, so the type that
 * describes an API response keeps all five.
 */
export const ADMISSION_TYPE_LABEL = {
  REGULAR: 'Regular',
  MANAGEMENT: 'Management quota',
  RTE: 'RTE quota',
  TRANSFER: 'Transfer from another institute',
  STAFF_WARD: 'Staff ward',
} as const;
export type AdmissionType = keyof typeof ADMISSION_TYPE_LABEL;

/** What the form offers, and all the server accepts on a write. */
export const SELECTABLE_ADMISSION_TYPES = ['REGULAR', 'TRANSFER'] as const;
export type SelectableAdmissionType = (typeof SELECTABLE_ADMISSION_TYPES)[number];
export const ADMISSION_TYPE_OPTIONS = SELECTABLE_ADMISSION_TYPES.map((value) => ({
  value,
  label: ADMISSION_TYPE_LABEL[value],
}));

/** Who the institute rings first. Not always a parent — hence the third option. */
export const PRIMARY_CONTACT_LABEL = {
  FATHER: 'Father',
  MOTHER: 'Mother',
  GUARDIAN: 'Guardian',
} as const;
export type PrimaryContact = keyof typeof PRIMARY_CONTACT_LABEL;
export const PRIMARY_CONTACT_OPTIONS = options(PRIMARY_CONTACT_LABEL);

/**
 * The papers an office collects at admission. `label` is what the parent is
 * asked for; `hint` says when it applies, so nobody hunts for a certificate
 * their child does not need.
 */
export const DOCUMENT_KINDS = [
  {
    key: 'birthCertificate',
    label: 'Birth certificate',
    hint: 'Proof of the date of birth entered above.',
  },
  {
    key: 'studentAadhaar',
    label: 'Student Aadhaar',
    hint: 'Needed for most government scholarships.',
  },
  {
    key: 'transferCertificate',
    label: 'Transfer certificate (TC)',
    hint: 'From the last institute attended. Needed for a transfer.',
  },
  {
    key: 'previousMarksMemo',
    label: 'Previous marks memo',
    hint: 'The last report card or marks statement. Needed for a transfer.',
  },
  {
    key: 'categoryCertificate',
    label: 'Category certificate',
    hint: 'Needed for an SC, ST, OBC or EWS claim.',
  },
] as const;

export type DocumentKind = (typeof DOCUMENT_KINDS)[number]['key'];

/**
 * The certificates an admission cannot be saved without.
 *
 * Every child proves their date of birth; a transfer also brings the TC and the
 * last marks memo, and a category claim needs its certificate. Mirrors
 * `requiredDocuments` in the server's `admissions.validation.ts`, which decides.
 */
export function requiredDocuments(record: {
  admissionType: SelectableAdmissionType;
  /** Blank while the form's category has not been chosen yet. */
  category: SocialCategory | '';
}): DocumentKind[] {
  const required: DocumentKind[] = ['birthCertificate'];
  if (record.admissionType === 'TRANSFER') required.push('transferCertificate', 'previousMarksMemo');
  // No certificate is asked for until a category has been chosen that needs one.
  if (record.category !== '' && record.category !== 'GENERAL') required.push('categoryCertificate');
  return required;
}

/**
 * What to tell the office about the new student's login.
 *
 * One wording, read by the admission screen and the bulk import, so the two
 * cannot describe the same outcome differently (CLAUDE.md §3). No password is
 * ever sent or shown — the student sets their own from a one-time link.
 */
export const STUDENT_ACCOUNT_NOTE: Record<StudentAccountOutcome, string> = {
  INVITED: 'An email is on its way with a link to set their password.',
  NO_EMAIL: 'No student email was given, so they cannot sign in yet.',
  EMAIL_TAKEN: 'That email already signs in at your institute, so no student login was opened.',
};

/**
 * And what to tell them about the parent's login, which is the one a young
 * child's family actually signs in with.
 */
export const PARENT_ACCOUNT_NOTE: Record<ParentAccountOutcome, string> = {
  INVITED: 'Their parent has been sent a link to the parent panel.',
  LINKED: 'Their parent already signs in here, so this child was added to that account.',
  NO_EMAIL: 'No parent email was given, so the family cannot sign in yet.',
  EMAIL_TAKEN: 'That parent email already signs in at your institute, so no parent login was opened.',
};

/** The same three, in the compact form a table cell has room for. */
export const STUDENT_ACCOUNT_LABEL: Record<StudentAccountOutcome, string> = {
  INVITED: 'Invite sent',
  NO_EMAIL: 'No login',
  EMAIL_TAKEN: 'Email in use',
};
